import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Paper,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  Stack
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import BusinessManagement from './BusinessManagement';
import SuperAdminUserManagement from './SuperAdminUserManagement';
import SystemAnalytics from './SystemAnalytics';
import BusinessCategories from './BusinessCategories';
import Footer from '../shared/Footer';
import api from '../../utils/api';

const SuperAdminDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [stats, setStats] = useState({
    totalBusinesses: 0,
    activeBusinesses: 0,
    totalUsers: 0,
    totalBranches: 0
  });

  useEffect(() => {
    fetchSystemStats();
  }, []);

  const fetchSystemStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/super-admin/stats');
      const backendStats = response.data.stats || {};
      
      // Map backend snake_case to frontend camelCase
      setStats({
        totalBusinesses: parseInt(backendStats.total_businesses) || 0,
        activeBusinesses: parseInt(backendStats.active_businesses) || 0,
        totalUsers: parseInt(backendStats.total_users) || 0,
        totalBranches: parseInt(backendStats.total_branches) || 0
      });
      setError(null);
    } catch (error) {
      console.error('Error fetching system stats:', error);
      setError('Failed to load system statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <AppBar 
          position="sticky" 
          sx={{ 
            backgroundColor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
          elevation={0}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 4, flexGrow: 1 }}>
                <StoreIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    letterSpacing: '.2rem',
                    color: 'primary.main',
                  }}
                >
                  POS SYSTEM - SUPER ADMIN
                </Typography>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          flexGrow: 1 
        }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <AppBar 
          position="sticky" 
          sx={{ 
            backgroundColor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
          elevation={0}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 4, flexGrow: 1 }}>
                <StoreIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    letterSpacing: '.2rem',
                    color: 'primary.main',
                  }}
                >
                  POS SYSTEM - SUPER ADMIN
                </Typography>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
          <Alert severity="error">
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  const StatCard = ({ icon, title, value, subtitle, color = 'primary' }) => (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        backgroundColor: theme.palette.background.default,
        border: 1,
        borderColor: theme.palette.divider,
        borderRadius: 2
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {React.cloneElement(icon, { 
            sx: { 
              color: theme.palette[color].main, 
              mr: 1,
              fontSize: 28
            } 
          })}
          <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
        </Box>
        <Typography 
          variant="h3" 
          component="div" 
          sx={{ 
            mb: 1,
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              display: 'flex',
              alignItems: 'center' 
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      {/* Super Admin Navbar */}
      <AppBar 
        position="sticky" 
        sx={{ 
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
        elevation={0}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo and Brand */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 4, flexGrow: 1 }}>
              <StoreIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  letterSpacing: '.2rem',
                  color: 'primary.main',
                  textDecoration: 'none',
                }}
              >
                POS SYSTEM - SUPER ADMIN
              </Typography>
            </Box>

            {/* User Section */}
            <Stack 
              direction="row" 
              spacing={2} 
              alignItems="center" 
              sx={{ flexShrink: 0 }}
            >
              {/* User Info */}
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' }, 
                alignItems: 'center', 
                gap: 1 
              }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Chip
                  label="SUPER ADMIN"
                  size="small"
                  sx={{ 
                    height: '24px',
                    fontWeight: 500,
                    backgroundColor: 'error.main',
                    color: 'white',
                  }}
                />
              </Box>

              {/* User Menu */}
              <Box>
                <Tooltip title="Account settings">
                  <IconButton onClick={handleOpenUserMenu}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {user?.firstName?.[0]?.toUpperCase() || 'S'}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem onClick={handleLogout}>
                    <Typography textAlign="center">Logout</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 1,
              fontWeight: 600,
              color: theme.palette.text.primary 
            }}
          >
            Super Admin Dashboard
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 2,
              color: theme.palette.text.secondary 
            }}
          >
            Welcome back, {user?.firstName}! System overview and management.
          </Typography>
        </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<BusinessIcon />}
            title="Total Businesses"
            value={stats.totalBusinesses}
            subtitle={`${stats.activeBusinesses} active`}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<PeopleIcon />}
            title="Total Users"
            value={stats.totalUsers}
            subtitle="Across all businesses"
            color="secondary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<StoreIcon />}
            title="Total Branches"
            value={stats.totalBranches}
            subtitle="All business locations"
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<AssessmentIcon />}
            title="System Health"
            value="Good"
            subtitle="All systems operational"
            color="info"
          />
        </Grid>
      </Grid>

      {/* Tabs Navigation */}
      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: theme.palette.divider,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ 
            borderBottom: 1,
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.background.default
          }}
        >
          <Tab
            label="Business Management"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              minWidth: { xs: 'auto', sm: 160 }
            }}
          />
          <Tab
            label="User Management"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              minWidth: { xs: 'auto', sm: 160 }
            }}
          />
          <Tab
            label="System Analytics"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              minWidth: { xs: 'auto', sm: 160 }
            }}
          />
          <Tab
            label="Business Categories"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              minWidth: { xs: 'auto', sm: 160 }
            }}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && <BusinessManagement />}
          {tabValue === 1 && <SuperAdminUserManagement />}
          {tabValue === 2 && <SystemAnalytics />}
          {tabValue === 3 && <BusinessCategories />}
        </Box>
      </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default SuperAdminDashboard;